/**
 * SystemDiagnostics - Admin component for system health monitoring
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { 
  Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Database, HardDrive, Mail, Server, Cpu, Clock, Users,
  CreditCard, Calendar, Activity, Award, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const config = {
    operational: { label: 'Operational', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    connected: { label: 'Connected', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    healthy: { label: 'Healthy', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    configured: { label: 'Configured', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    ready: { label: 'Ready', className: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    log_mode: { label: 'Log Mode', className: 'bg-blue-100 text-blue-800', icon: AlertTriangle },
    degraded: { label: 'Degraded', className: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
    not_configured: { label: 'Not Configured', className: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
    unknown: { label: 'Unknown', className: 'bg-gray-100 text-gray-800', icon: AlertTriangle },
    error: { label: 'Error', className: 'bg-red-100 text-red-800', icon: XCircle },
  };

  const { label, className, icon: Icon } = config[status] || config.error;

  return (
    <Badge className={`${className} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
};

const SystemDiagnostics = () => {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningTest, setRunningTest] = useState(null);

  // Get token from zustand store with fallback
  const getToken = () => {
    try {
      const authStorage = localStorage.getItem('eventflow-auth');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        return parsed?.state?.token || localStorage.getItem('eventflow_token');
      }
    } catch (e) {
      // Fallback
    }
    return localStorage.getItem('eventflow_token');
  };

  const fetchDiagnostics = async () => {
    setLoading(true);
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/diagnostics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDiagnostics(data);
    } catch (err) {
      console.error('Failed to fetch diagnostics:', err);
      toast.error('Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiagnostics();
  }, []);

  const runTest = async (testName) => {
    setRunningTest(testName);
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/diagnostics/test?test=${testName}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.status === 'pass' || data.results) {
        toast.success(`${testName} test passed`);
      } else {
        toast.error(`${testName} test failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      toast.error(`Failed to run ${testName} test`);
    } finally {
      setRunningTest(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Running diagnostics...</span>
        </CardContent>
      </Card>
    );
  }

  const stats = diagnostics?.statistics || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">System Diagnostics</h2>
          <p className="text-muted-foreground">
            Last checked: {diagnostics?.timestamp ? new Date(diagnostics.timestamp).toLocaleString() : 'N/A'}
          </p>
        </div>
        <Button onClick={fetchDiagnostics} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Service Status Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <Database className="h-5 w-5 text-blue-600" />
              <StatusBadge status={diagnostics?.database?.status} />
            </div>
            <h3 className="font-semibold">Database</h3>
            <p className="text-sm text-muted-foreground">
              {diagnostics?.database?.counts?.delegates || 0} delegates
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <HardDrive className="h-5 w-5 text-purple-600" />
              <StatusBadge status={diagnostics?.storage?.exists ? 'healthy' : 'error'} />
            </div>
            <h3 className="font-semibold">Storage</h3>
            <p className="text-sm text-muted-foreground">
              {diagnostics?.storage?.total_size_mb || 0}MB used
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <Mail className="h-5 w-5 text-green-600" />
              <StatusBadge status={diagnostics?.services?.email?.status} />
            </div>
            <h3 className="font-semibold">Email</h3>
            <p className="text-sm text-muted-foreground">
              {diagnostics?.services?.email?.provider || 'Not configured'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <Server className="h-5 w-5 text-orange-600" />
              <StatusBadge status={diagnostics?.services?.ai?.status} />
            </div>
            <h3 className="font-semibold">AI Service</h3>
            <p className="text-sm text-muted-foreground">
              {diagnostics?.services?.ai?.provider || 'Not configured'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Application Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Events
              </div>
              <div className="text-3xl font-bold">{diagnostics?.database?.counts?.events || 0}</div>
              <p className="text-sm text-muted-foreground">
                Total events
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-4 w-4" />
                Delegates
              </div>
              <div className="text-3xl font-bold">{diagnostics?.database?.counts?.delegates || 0}</div>
              <p className="text-sm text-muted-foreground">
                Registered
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Award className="h-4 w-4" />
                Certificates
              </div>
              <div className="text-3xl font-bold">{diagnostics?.database?.counts?.certificates || 0}</div>
              <p className="text-sm text-muted-foreground">
                Issued
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <FileText className="h-4 w-4" />
                Audit Logs
              </div>
              <div className="text-3xl font-bold">{diagnostics?.database?.counts?.audit_logs || 0}</div>
              <p className="text-sm text-muted-foreground">
                Records
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(diagnostics?.recent_activity || []).map((activity, idx) => (
              <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                <span className="font-medium">{activity.action}</span>
                <span className="text-sm text-muted-foreground">
                  {new Date(activity.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
            {(!diagnostics?.recent_activity || diagnostics.recent_activity.length === 0) && (
              <p className="text-sm text-muted-foreground">No recent activity</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Info */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Database Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Database Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type</span>
              <span className="font-medium">{diagnostics?.services?.database?.type || 'MongoDB'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <StatusBadge status={diagnostics?.database?.status} />
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Events</span>
              <span className="font-medium">{diagnostics?.database?.counts?.events || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delegates</span>
              <span className="font-medium">{diagnostics?.database?.counts?.delegates || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Sessions</span>
              <span className="font-medium">{diagnostics?.database?.counts?.sessions || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payments</span>
              <span className="font-medium">{diagnostics?.database?.counts?.payments || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* System Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              System Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Platform</span>
              <span className="font-medium">{diagnostics?.system?.platform || 'Unknown'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Architecture</span>
              <span className="font-medium">{diagnostics?.system?.architecture || 'Unknown'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Storage Directory</span>
              <span className="font-medium">{diagnostics?.storage?.exists ? 'Configured' : 'Not Found'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Files Stored</span>
              <span className="font-medium">{diagnostics?.storage?.file_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Storage Used</span>
              <span className="font-medium">{diagnostics?.storage?.total_size_mb || 0} MB</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Service Test Buttons */}
      <Card>
        <CardHeader>
          <CardTitle>Service Tests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              onClick={() => runTest('database')}
              disabled={runningTest === 'database'}
            >
              {runningTest === 'database' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
              Test Database
            </Button>
            <Button 
              variant="outline" 
              onClick={() => runTest('email')}
              disabled={runningTest === 'email'}
            >
              {runningTest === 'email' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Test Email
            </Button>
            <Button 
              variant="outline" 
              onClick={() => runTest('ai')}
              disabled={runningTest === 'ai'}
            >
              {runningTest === 'ai' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Server className="h-4 w-4 mr-2" />}
              Test AI
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemDiagnostics;
